(() => {
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  const createGroupCode = () => {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };

  const groupCodeForProduct = async (root) => {
    try {
      const response = await fetch(root.dataset.cartJsonUrl, { headers: { Accept: 'application/json' } });
      if (response.ok) {
        const cart = await response.json();
        const productId = Number(root.dataset.productId);
        const existing = (Array.isArray(cart.items) ? cart.items : []).find((item) => (
          Number(item.product_id) === productId
          && String(item.properties?._delia_tasting_booking || '') === '1'
          && UUID_PATTERN.test(String(item.properties?._delia_tasting_order_code || ''))
        ));
        if (existing) return String(existing.properties._delia_tasting_order_code).toLowerCase();
      }
    } catch (error) {
      console.warn('[delia-category-picker] cart lookup failed, creating a new group code', error);
    }
    return createGroupCode();
  };

  const initialize = (root) => {
    if (!root || root.dataset.categoryPickerReady === 'true') return;
    root.dataset.categoryPickerReady = 'true';

    const isGerman = String(root.dataset.locale || '').toLowerCase().startsWith('de');
    const copyNode = root.querySelector('[data-category-copy]');
    const copy = copyNode ? JSON.parse(copyNode.textContent) : {};
    const options = [...root.querySelectorAll('[data-category-option]')];
    const quantity = root.querySelector('[data-category-quantity]');
    const minus = root.querySelector('[data-category-minus]');
    const plus = root.querySelector('[data-category-plus]');
    const add = root.querySelector('[data-category-add]');
    const summary = root.querySelector('[data-category-summary]');
    const total = root.querySelector('[data-category-total]');
    const status = root.querySelector('[data-category-status]');
    const formatter = new Intl.NumberFormat(isGerman ? 'de-DE' : 'en-US', {
      style: 'currency', currency: root.dataset.currency || 'EUR'
    });
    let selected = null;

    const normalizedQuantity = () => {
      if (!selected) return 1;
      const maximum = Number(selected.dataset.available || 0);
      const value = Number.parseInt(quantity.value, 10);
      return Math.min(maximum, Math.max(1, Number.isFinite(value) ? value : 1));
    };

    const update = () => {
      if (!selected) {
        summary.textContent = isGerman ? copy.none : copy.noneEn;
        total.textContent = '';
        quantity.disabled = true;
        minus.disabled = true;
        plus.disabled = true;
        add.disabled = true;
        return;
      }
      const count = normalizedQuantity();
      const maximum = Number(selected.dataset.available || 0);
      quantity.value = String(count);
      quantity.max = String(maximum);
      quantity.disabled = false;
      minus.disabled = count <= 1;
      plus.disabled = count >= maximum;
      add.disabled = maximum < 1;
      const ticketLabel = count === 1 ? copy.ticket : copy.tickets;
      summary.textContent = `${selected.dataset.categoryLabel} · ${count} ${ticketLabel}`;
      total.textContent = formatter.format((Number(selected.dataset.priceCents || 0) * count) / 100);
    };

    options.filter((option) => !option.disabled).forEach((option) => {
      option.addEventListener('click', () => {
        options.forEach((candidate) => candidate.setAttribute('aria-pressed', 'false'));
        selected = option;
        selected.setAttribute('aria-pressed', 'true');
        quantity.value = '1';
        status.textContent = '';
        update();
      });
    });
    minus.addEventListener('click', () => { quantity.value = String(normalizedQuantity() - 1); update(); });
    plus.addEventListener('click', () => { quantity.value = String(normalizedQuantity() + 1); update(); });
    quantity.addEventListener('input', update);
    quantity.addEventListener('change', update);

    add.addEventListener('click', async () => {
      if (!selected || selected.disabled) return;
      const count = normalizedQuantity();
      add.disabled = true;
      status.textContent = isGerman ? copy.adding : copy.addingEn;
      try {
        const groupCode = await groupCodeForProduct(root);
        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{
              id: Number(selected.dataset.variantId),
              quantity: count,
              properties: {
                _delia_tasting_booking: '1',
                _delia_tasting_order_code: groupCode,
                _delia_tasting_category: selected.dataset.categoryCode || '',
                _delia_tasting_category_label: selected.dataset.categoryLabel || ''
              }
            }]
          })
        });
        if (!response.ok) throw new Error(`cart_add_${response.status}`);
        window.location.assign(root.dataset.cartUrl);
      } catch (error) {
        console.error('[delia-category-picker] add failed', error);
        status.textContent = isGerman ? copy.error : copy.errorEn;
        update();
      }
    });

    update();
  };

  const initializeAll = (scope = document) => scope.querySelectorAll('[data-delia-category-picker]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
